/**
 * AI Module Studio Service.
 *
 * Handles intelligent multi-turn module generation, natural language refinements,
 * document source extraction, and Firestore draft persistence.
 */

import { doc, getDoc, setDoc, collection, getDocs, query, where, orderBy, deleteDoc, serverTimestamp, deleteField } from 'firebase/firestore'
import { db, auth, APP_COLLECTION, APP_DOC } from './firebase.js'
import { createDefaultModule, validateModuleSchema, extractDocLinks, sanitizeForFirestore, hydrateFromFirestore } from '../data/moduleSchema.js'
import { modules as builtInModules, SEED_COURSES as builtInCourses } from '../data/modules.js'
import { uniqueModuleSlug } from '../data/courses.js'

export { extractDocLinks, sanitizeForFirestore, hydrateFromFirestore }

/**
 * Calls secure server-side Cloud Function proxy (/api/generate-module).
 * The API key is stored securely in GCP Secret Manager and never exposed to the client.
 */
async function callSecureBackendModuleGenerator({ prompt, currentSchema, conversationHistory, sourceUrl, image }) {
  let idToken = ''
  try {
    if (auth && auth.currentUser) {
      idToken = await auth.currentUser.getIdToken()
    }
  } catch (tokenErr) {
    console.warn('Could not retrieve auth token:', tokenErr)
  }

  const response = await fetch('/api/generate-module', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({
      prompt,
      currentSchema,
      conversationHistory,
      sourceUrl,
      image,
    }),
  })

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}))
    throw new Error(errData.error || `Server returned status ${response.status}`)
  }

  const data = await response.json()
  if (!data || !data.schema) {
    throw new Error('Invalid response structure from module generator server')
  }

  const { normalized } = validateModuleSchema(data.schema)
  return {
    schema: normalized,
    assistantMessage: data.assistantMessage,
  }
}

/**
 * Intelligent prompt-to-schema generator.
 * Securely calls server-side Cloud Function proxy, falling back to local builder if offline.
 */
export async function generateOrRefineModule({
  prompt,
  currentSchema = null,
  conversationHistory = [],
  sourceUrl = '',
  image = null,
}) {
  const cleanPrompt = (prompt || '').trim()
  const links = extractDocLinks(cleanPrompt)
  const resolvedSourceUrl = sourceUrl || links.googleDocUrl || links.confluenceUrl || links.genericUrl || ''

  // 1. Attempt secure backend generation (API key protected on server)
  try {
    const result = await callSecureBackendModuleGenerator({
      prompt: cleanPrompt || (image ? 'Analyze this attached screenshot and generate or refine the Finder University training module based on it.' : ''),
      currentSchema,
      conversationHistory,
      sourceUrl: resolvedSourceUrl,
      image,
    })
    if (result && result.schema) {
      return result
    }
  } catch (err) {
    console.warn('Secure server-side generation error, using fallback:', err)
  }

  // --- Fallback Local Generator ---
  const existingCourse = currentSchema?.track || ''
  const existingTitle = currentSchema?.title || ''
  const historyText = Array.isArray(conversationHistory)
    ? conversationHistory.map((m) => (typeof m.content === 'string' ? m.content : JSON.stringify(m.content))).join(' ')
    : ''
  const combinedContext = `${cleanPrompt} ${existingTitle} ${existingCourse} ${historyText}`.toLowerCase()

  // Plurals, consistently. These already allowed one on `sheets?`, `sumifs?`,
  // `apps? script` and `kids?`, so the intent was there — it was just missed on
  // most of the rest. `\b` makes that omission silent and total: `formula\b`
  // does not match "formulas", so "a module on pivot tables" and "explain
  // lookup formulas" were not classified as Sheets AT ALL, in a Sheets training
  // app, and quietly got the generic template instead.
  //
  // `pr` is deliberately left alone rather than widened to `prs?`. It is
  // already the loosest token here — two letters that appear as a word in
  // plenty of non-git prose — and making it looser is the wrong direction.
  const isGithub = /\b(github|git|branch(?:es)?|repos?|repositor(?:y|ies)|pull requests?|pr|commits?|merges?)\b/i.test(combinedContext)
  const isKidsOrBeginner = /\b(primary school|elementary|kids?|child(?:ren)?|non-developer|non-technical|beginners?|basics|simple|5 year old|for dummies)\b/i.test(combinedContext)
  const isAppsScript = /\b(apps? scripts?|javascript|macros?|triggers?|automations?)\b/i.test(combinedContext)
  const isAI = /\b(claude|ai|gpt|llms?|prompting|prompt engineering|chatgpt|gemini)\b/i.test(combinedContext)
  const isSheets = /\b(sheets?|excel|vlookups?|xlookups?|sumifs?|index match(?:es)?|formulas?|pivot tables?)\b/i.test(combinedContext)

  let generatedTitle = existingTitle || 'Interactive Training Masterclass'
  let track = existingCourse || (isGithub ? 'github' : isAppsScript ? 'apps-script' : isAI ? 'ai-finance' : 'sheets')
  let phase = currentSchema?.phase || 'Applied Skills'
  let phaseColor = currentSchema?.phaseColor || 'indigo'
  let pages = []
  let quiz = { passingScore: 80, questions: [] }

  if (isGithub) {
    track = 'github'
    phase = isKidsOrBeginner ? 'Foundations for Everyone' : 'Applied Skills'
    phaseColor = 'purple'

    if (isKidsOrBeginner) {
      generatedTitle = existingTitle && existingTitle.toLowerCase().includes('git') ? existingTitle : 'GitHub 101: Repositories, Branches & Syncing'
      pages = [
        {
          title: '1. The Core Building Blocks',
          subtitle: 'Understanding Repos, Branches, Main, and Commits in simple terms',
          content: `Git records every change made to a project over time, while GitHub is the cloud platform where your team collaborates.

Here are the 4 fundamental terms in plain English:`,
          table: {
            headers: ['GitHub Term', 'What It Is in Coding', 'In Simple Terms'],
            rows: [
              ['Repository (Repo)', 'The project folder and database', 'The App\'s Home: Contains all code, assets, settings, and permanent history'],
              ['Main Branch', 'The default production-ready timeline', 'The Live App: Stable, tested code running for users'],
              ['Branch', 'An isolated copy of code for new work', 'A Safe Sandbox: Build and test features without breaking live code'],
              ['Commit', 'A permanent recorded snapshot', 'A Saved Checkpoint: Timestamped save point with an explanatory message'],
            ],
          },
          callout: {
            type: 'key',
            text: 'Golden Rule: Never edit the main branch directly. Always create a branch, test your changes, and merge once verified.',
          },
          questions: [
            {
              question: 'Why do developers create a branch instead of editing the main branch directly?',
              options: [
                'To work in an isolated sandbox and avoid breaking the stable live application',
                'Because Git only allows one person to view the repository at a time',
                'To permanently hide their work from teammates',
              ],
              answer: 0,
              explanation: 'Branches isolate experimental work so you can test freely without risking production code.',
            },
          ],
        },
        {
          title: '2. Where Code Lives: Local Laptop vs GitHub Cloud',
          subtitle: 'Why local commits stay private until you push them',
          content: `Git is a distributed system, which means code lives in two distinct locations:

* **Your Local Machine (Laptop)**: Project files live in your local folder. All local branches and history are saved inside the hidden \`.git\` directory. Everything you do locally is instant, offline, and 100% private.
* **GitHub Cloud (Remote)**: The central repository on \`github.com\` visible to teammates, running automated tests (CI/CD) and handling Pull Requests.`,
          diagram: {
            title: 'Local vs Remote Architecture',
            steps: [
              { title: '1. Local Laptop', description: 'Working files + hidden .git history (private)', icon: 'zap', color: 'blue' },
              { title: '2. git push / git pull', description: 'The sync bridge between laptop & cloud', icon: 'database', color: 'purple' },
              { title: '3. GitHub Cloud', description: 'Central team repository on github.com', icon: 'chart', color: 'green' },
            ],
          },
          callout: {
            type: 'tip',
            text: 'Commits on your laptop remain safe on your hard drive, but teammates cannot see them until you run git push.',
          },
          questions: [
            {
              question: 'You create a branch and make 3 commits on your laptop. Who can see these changes before you push?',
              options: [
                'Only you on your local machine',
                'Everyone on GitHub automatically',
                'The deployment server',
              ],
              answer: 0,
              explanation: 'Local branches and commits remain strictly on your machine until you push them to GitHub.',
            },
          ],
        },
        {
          title: '3. The Bridge: Pushing, Pulling & Pull Requests',
          subtitle: 'How changes flow from your laptop to the live project',
          content: `To synchronize code between your laptop and GitHub, you use two fundamental actions:

1. **\`git pull\`** (Download from GitHub): Fetches the newest approved code from GitHub down to your laptop. Always pull before creating a new branch!
2. **\`git push\`** (Upload to GitHub): Sends your local branch and commits up to GitHub so teammates can review your work.
3. **Pull Request (PR)**: Proposes merging your uploaded branch into \`main\`, enabling team review and automated testing.`,
          callout: {
            type: 'key',
            text: '5-Step Workflow: Pull latest main -> Create branch -> Commit checkpoints -> Push to GitHub -> Open PR & Merge.',
          },
          questions: [
            {
              question: 'What is the purpose of running "git pull"?',
              options: [
                'Downloads the latest updates from GitHub down to your laptop',
                'Uploads your local files to GitHub',
                'Deletes your repository',
              ],
              answer: 0,
              explanation: 'git pull downloads changes from the remote repository so your local code stays up to date.',
            },
          ],
        },
      ]

      quiz = {
        passingScore: 80,
        questions: [
          {
            question: 'Why do developers create a branch instead of editing the main branch directly?',
            options: [
              'To work in an isolated sandbox and avoid breaking the stable live application',
              'Because Git only allows one person to view the repository at a time',
              'To permanently hide their work from teammates',
            ],
            answer: 0,
            explanation: 'Branches isolate experimental work so you can test freely without risking production code.',
          },
          {
            question: 'You create a branch and make 3 commits on your laptop. Who can see these changes before you push?',
            options: [
              'Only you on your local machine',
              'Everyone on GitHub automatically',
              'The deployment server',
            ],
            answer: 0,
            explanation: 'Local branches and commits remain strictly on your machine until you push them to GitHub.',
          },
          {
            question: 'What is the purpose of running "git pull"?',
            options: [
              'Downloads the latest updates from GitHub down to your laptop',
              'Uploads your local files to GitHub',
              'Deletes your repository',
            ],
            answer: 0,
            explanation: 'git pull downloads changes from the remote repository so your local code stays up to date.',
          },
        ],
      }
    } else {
      generatedTitle = 'Git & GitHub Fundamentals for Teams'
      pages = [
        {
          title: '1. Core Git Architecture & Lifecycle',
          subtitle: 'Working Directory, Staging Area, and Remote Repositories',
          content: `Git SEED_COURSES project history as a tree of immutable commit snapshots.\n\nUnderstanding the three local states (Working Tree -> Index/Staging -> Commit History) is key to navigating version control safely.`,
          diagram: {
            title: 'Git State Flow',
            steps: [
              { title: 'Working Directory', description: 'Unstaged local edits', icon: 'database', color: 'blue' },
              { title: 'Staging Area', description: 'git add (indexed files)', icon: 'zap', color: 'yellow' },
              { title: 'Remote Repository', description: 'git push to GitHub main/feature branch', icon: 'chart', color: 'green' },
            ],
          },
          callout: { type: 'key', text: 'Atomic commits with descriptive commit messages make rollbacks and code reviews seamless.' },
          questions: [
            {
              question: 'What command moves changes from the Working Directory into the Staging Area?',
              options: ['git add', 'git push', 'git commit'],
              answer: 0,
              explanation: 'git add stages modified files for the next commit snapshot.',
            },
          ],
        },
        {
          title: '2. Essential Team Commands',
          subtitle: 'Branching, Stashing, Rebasing, and Merging',
          content: `Daily Git workflows center around clean branching strategies (e.g. Trunk-Based Development or GitHub Flow).\n\nKeep branches short-lived and rebase or pull regularly to minimize merge conflicts.`,
          callout: { type: 'tip', text: 'Use conventional commits (feat:, fix:, chore:) to automate changelogs.' },
          questions: [
            {
              question: 'What is the recommended practice to minimize merge conflicts with team members?',
              options: ['Pull or rebase from main regularly', 'Never push your branch', 'Only write code on the weekend'],
              answer: 0,
              explanation: 'Frequently syncing with main keeps your branch up to date and minimizes divergent code conflicts.',
            },
          ],
        },
      ]
      quiz = {
        passingScore: 80,
        questions: [
          {
            question: 'What command moves changes from the Working Directory into the Staging Area?',
            options: ['git add', 'git push', 'git commit'],
            answer: 0,
            explanation: 'git add stages modified files for the next commit snapshot.',
          },
        ],
      }
    }
  // `combinedContext`, not `lowerPrompt` — which is declared nowhere in this
  // file. A rename missed this one branch, and short-circuiting hid it: the
  // second operand is only evaluated when isSheets is true, so the fallback
  // threw ReferenceError for exactly the prompts this app is most likely to
  // get ("pivot table", "formula", "vlookup") and worked for everything else.
  } else if (isSheets && (combinedContext.includes('xlookup') || combinedContext.includes('vlookup') || combinedContext.includes('lookup'))) {
    generatedTitle = 'Mastering XLOOKUP: Fast, Safe Table Lookups'
    track = 'sheets'
    phase = 'Applied Skills'
    phaseColor = 'blue'
    pages = [
      {
        title: '1. Why XLOOKUP Replaced VLOOKUP',
        subtitle: 'Bidirectional lookups, default match modes, and built-in error handling',
        content: `Traditional VLOOKUP broke whenever columns were inserted or reordered. XLOOKUP completely eliminates column index counting and supports searching from right-to-left.`,
        callout: { type: 'key', text: 'XLOOKUP only references the search column and the return column, making your sheets immune to column additions.' },
        questions: [
          {
            question: 'What makes XLOOKUP safer than VLOOKUP when columns are inserted into a dataset?',
            options: [
              'It uses direct column references instead of fixed numerical column index offsets',
              'It only works with numbers',
              'It encrypts the spreadsheet cells',
            ],
            answer: 0,
            explanation: 'XLOOKUP refers to specific column ranges directly, so adding or moving columns never breaks the lookup formula.',
          },
        ],
      },
    ]
  } else if (isAI) {
    generatedTitle = 'Prompting Claude for Clean Financial Analysis'
    track = 'ai-finance'
    phase = 'Applied Skills'
    phaseColor = 'purple'
    pages = [
      {
        title: '1. Structured Prompting for Commercial Teams',
        subtitle: 'Context, Constraints, and Output Format',
        content: `Effective AI workflows require structured prompting: Provide source schema, specify edge case rules, and enforce JSON or markdown table outputs.`,
        callout: { type: 'tip', text: 'Always specify currency rounding and timezone handling explicitly in your system prompts.' },
        questions: [
          {
            question: 'What is the most effective way to ensure predictable AI data outputs?',
            options: [
              'Define explicit schemas, constraints, and output format examples in the prompt',
              'Send one-word prompts',
              'Ask the AI to guess the column headers',
            ],
            answer: 0,
            explanation: 'Explicit schemas, edge case rules, and output format constraints produce deterministic, high-accuracy results.',
          },
        ],
      },
    ]
  } else {
    // Dynamic generation from general user prompt
    const cleanSubject = cleanPrompt
      .replace(/^(can you please |please |build a module that |build a module |create a course on |explain )/i, '')
      .slice(0, 60)
      .trim()
    generatedTitle = cleanSubject.charAt(0).toUpperCase() + cleanSubject.slice(1)
    track = 'general'
    phase = isKidsOrBeginner ? 'Foundations' : 'Core Training'
    phaseColor = 'indigo'

    pages = [
      {
        title: `1. Overview & Core Concepts: ${generatedTitle}`,
        subtitle: isKidsOrBeginner ? 'A friendly, step-by-step introduction' : 'Business context and core principles',
        content: `This training module breaks down **${generatedTitle}** into simple, actionable steps.\n\nWhether you are reviewing internal SOPs or learning the fundamentals for the first time, this module provides the foundational knowledge, practical examples, and verification steps you need.`,
        diagram: {
          title: 'Step-by-Step Learning Path',
          steps: [
            { title: '1. Core Concepts', description: 'Understanding the fundamentals and vocabulary', icon: 'zap', color: 'blue' },
            { title: '2. Practical Application', description: 'Real-world examples and standard workflows', icon: 'database', color: 'green' },
            { title: '3. Knowledge Check', description: 'Interactive recap checks to certify your skills', icon: 'chart', color: 'purple' },
          ],
        },
        callout: {
          type: 'key',
          text: `Mastering ${generatedTitle} ensures consistent best practices and empowers cross-functional collaboration.`,
        },
        questions: [
          {
            question: `What is the primary benefit of following standard best practices for ${generatedTitle}?`,
            options: [
              'Ensures consistency, reduces errors, and improves team efficiency',
              'Makes the computer run backwards',
              'Eliminates the need for team communication',
            ],
            answer: 0,
            explanation: 'Standardized workflows and clear guidelines prevent mistakes and maintain high data quality.',
          },
        ],
      },
      {
        title: '2. Practical Walkthrough & Best Practices',
        subtitle: 'Standard operating procedures and tips',
        content: `Here are the top guidelines to follow:\n\n1. **Start with Clean Inputs**: Verify documentation and prerequisites before starting.\n2. **Follow Verified Procedures**: Use established templates to prevent manual errors.\n3. **Review & Iterate**: Always double-check results and test edge cases.`,
        callout: {
          type: 'tip',
          text: 'Bookmark this module or download the reference guide to review these steps anytime.',
        },
        questions: [
          {
            question: 'Why should you verify documentation and inputs before executing a workflow?',
            options: [
              'To avoid processing bad data and prevent downstream rework',
              'To make the task take longer',
              'Because spreadsheets require paper printouts',
            ],
            answer: 0,
            explanation: 'Validating inputs at the start prevents compounding errors downstream.',
          },
        ],
      },
    ]

    quiz = {
      passingScore: 80,
      questions: [
        {
          question: `What is the primary benefit of following standard best practices for ${generatedTitle}?`,
          options: [
            'Ensures consistency, reduces errors, and improves team efficiency',
            'Makes the computer run backwards',
            'Eliminates the need for team communication',
          ],
          answer: 0,
          explanation: 'Standardized workflows and clear guidelines prevent mistakes and maintain high data quality.',
        },
      ],
    }
  }

  const generated = createDefaultModule({
    id: `custom-${generatedTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`,
    title: generatedTitle,
    track,
    phase,
    phaseColor,
    sourceDocUrl: resolvedSourceUrl,
    description: `A practical, step-by-step training module generated from: "${cleanPrompt}"`,
    pages,
    quiz,
  })

  const { normalized } = validateModuleSchema(generated)

  let assistantMsg = `I've generated a ${normalized.pages.length}-page interactive module titled "${normalized.title}".`
  if (resolvedSourceUrl) {
    assistantMsg += ` I've ingested the documentation from ${resolvedSourceUrl} and structured it into concepts, worked examples, and a knowledge quiz.`
  }
  assistantMsg += `\n\nYou can test it out on the right! Feel free to ask me to adjust anything (e.g. "Add another diagram" or "Add 2 more quiz questions").`

  return {
    schema: normalized,
    assistantMessage: assistantMsg,
  }
}


/**
 * Saves a module draft to Firestore under /module_drafts/{draftId}.
 */
export async function saveDraft(draftId, draftData, user) {
  try {
    const canonicalId = String(draftData?.currentSchema?.id || draftId)
    const docRef = doc(db, APP_COLLECTION, APP_DOC, 'module_drafts', canonicalId)
    const sanitizedData = sanitizeForFirestore(draftData)
    const payload = {
      ...sanitizedData,
      draftId: canonicalId,
      updatedAt: new Date().toISOString(),
      updatedBy: user?.uid || 'anon',
      creatorEmail: user?.email || '',
    }
    await setDoc(docRef, payload, { merge: true })

    // If draftId was a temporary ID different from canonicalId, delete the temporary document
    if (draftId && String(draftId) !== canonicalId && String(draftId).startsWith('draft-')) {
      const oldRef = doc(db, APP_COLLECTION, APP_DOC, 'module_drafts', String(draftId))
      await deleteDoc(oldRef).catch(() => {})
    }

    return { success: true, canonicalDraftId: canonicalId }
  } catch (err) {
    console.error('Failed to save draft:', err)
    return { success: false, error: err.message }
  }
}

/**
 * Loads a module draft from Firestore.
 */
export async function loadDraft(draftId) {
  try {
    const docRef = doc(db, APP_COLLECTION, APP_DOC, 'module_drafts', draftId)
    const snap = await getDoc(docRef)
    if (!snap.exists()) return null
    return hydrateFromFirestore(snap.data())
  } catch (err) {
    console.error('Failed to load draft:', err)
    return null
  }
}

/**
 * Loads any module for editing in AI Studio by ID or draft ID.
 * Looks up in order:
 * 1. Active Firestore draft (`module_drafts/{id}`)
 * 2. Published Firestore module (`custom_modules/{id}`)
 * 3. Built-in module from registry (`data/modules.js`), generating a clean starter schema.
 */
export async function loadModuleForStudio(idOrDraftId) {
  if (!idOrDraftId) return null
  const strId = String(idOrDraftId)

  // 1. Check module_drafts first (active unpublished work)
  if (typeof window !== 'undefined') {
    try {
      const draftRef = doc(db, APP_COLLECTION, APP_DOC, 'module_drafts', strId)
      const draftSnap = await getDoc(draftRef)
      if (draftSnap.exists()) {
        const data = hydrateFromFirestore(draftSnap.data())
        if (data?.currentSchema) {
          return {
            currentSchema: data.currentSchema,
            messages: Array.isArray(data.messages) ? data.messages : [],
            draftId: strId,
            sourceType: 'draft',
          }
        }
      }
    } catch (err) {
      // Ignore network/offline errors during background lookup
    }

    // 2. Check custom_modules in Firestore (published module)
    try {
      const customRef = doc(db, APP_COLLECTION, APP_DOC, 'custom_modules', strId)
      const customSnap = await getDoc(customRef)
      if (customSnap.exists()) {
        // Doc id last, so Studio republishes to the document it opened
        // rather than to whatever id the payload happened to carry.
        const data = hydrateFromFirestore({ ...customSnap.data(), id: customSnap.id })
        return {
          currentSchema: data,
          messages: [],
          draftId: strId,
          sourceType: 'published',
        }
      }
    } catch (err) {
      // Ignore network/offline errors during background lookup
    }
  }

  // 3. Check built-in modules in modules.js
  const builtIn = builtInModules.find(
    (m) => String(m.id) === strId || String(m.slug) === strId
  )
  if (builtIn) {
    const courseObj = builtInCourses.find((t) => t.id === builtIn.track)
    const courseName = courseObj ? courseObj.name : builtIn.track || 'General'

    const starterSchema = createDefaultModule({
      id: String(builtIn.id),
      title: builtIn.title,
      subtitle: builtIn.subtitle || '',
      description: builtIn.description || '',
      track: builtIn.track || 'sheets',
      phase: builtIn.phase || 'Core Skills',
      phaseColor: builtIn.phaseColor || 'indigo',
      estimatedMinutes: builtIn.optional ? 10 : 15,
      pages: [
        {
          title: `1. Overview: ${builtIn.title}`,
          subtitle: builtIn.subtitle || 'Foundational principles and workflow application',
          content: `## ${builtIn.title}\n\n${builtIn.description}\n\n### Key Learning Objectives\n- Understand the core concepts behind **${builtIn.title}**\n- Apply standardized techniques and avoid common errors\n- Test your knowledge with interactive checks and practice exercises`,
          callout: {
            type: 'key',
            text: `Best Practice: Master ${builtIn.title} in the ${courseName} track to streamline reporting and ensure accuracy across your workflows.`,
          },
        },
      ],
      interactive: {
        type: 'sheets_practice',
        instructions: 'Practice the formulas and techniques covered in this lesson directly in the grid below.',
        columns: ['A', 'B', 'C', 'D'],
        initialData: [
          ['Item', 'Category', 'Quantity', 'Amount'],
          ['Record 1', 'Standard', '10', '$150.00'],
          ['Record 2', 'Priority', '5', '$320.00'],
          ['Record 3', 'Standard', '12', '$180.00'],
        ],
      },
      quiz: {
        passingScore: 80,
        questions: [
          {
            question: `What is the primary objective of ${builtIn.title}?`,
            options: [
              builtIn.description || 'To improve data accuracy and automation efficiency',
              'To delete formulas',
              'To manually type values into cells',
            ],
            answer: 0,
            explanation: builtIn.description || 'Understanding the core concept ensures reliable, high-quality reporting.',
          },
        ],
      },
    })

    return {
      currentSchema: starterSchema,
      messages: [],
      draftId: String(builtIn.id),
      sourceType: 'builtin',
    }
  }

  return null
}

/**
 * Publishes a custom module into /custom_modules/{moduleId} so it becomes
 * part of the live training catalog.
 */
export async function publishCustomModule(moduleId, moduleData, user) {
  try {
    const docRef = doc(db, APP_COLLECTION, APP_DOC, 'custom_modules', String(moduleId))

    // A readable address, minted ONCE and never recomputed.
    //
    // Only when there is no slug yet. Renaming a module must NOT move its URL:
    // a link written into a lesson, a bookmark, a link pasted to a colleague
    // all outlive the title. So this is assigned on first publish and left
    // alone on every publish after, including a retitle.
    //
    // Unique against every existing slug AND every existing id, since both are
    // addresses in the same space. Without one the URL falls back to the id,
    // which is `custom-mti94cvy` — a base36 timestamp, correct and unreadable.
    const existingSlug = moduleData?.slug
    let slug = existingSlug
    if (!slug) {
      const published = await loadPublishedCustomModules()
      slug = uniqueModuleSlug(moduleData?.title, [...builtInModules, ...published], moduleId)
    }

    const sanitizedData = sanitizeForFirestore(moduleData)
    const payload = {
      ...sanitizedData,
      ...(slug ? { slug } : {}),
      status: 'published',
      publishedAt: new Date().toISOString(),
      publishedBy: {
        uid: user?.uid || '',
        displayName: user?.displayName || user?.email || 'Manager',
        email: user?.email || '',
      },
    }
    await setDoc(docRef, payload)
    return { success: true }
  } catch (err) {
    console.error('Failed to publish custom module:', err)
    return { success: false, error: err.message }
  }
}

/**
 * Loads all published custom modules from Firestore.
 */
export async function loadPublishedCustomModules() {
  try {
    const colRef = collection(db, APP_COLLECTION, APP_DOC, 'custom_modules')
    const snap = await getDocs(colRef)
    return snap.docs.map((d) => hydrateFromFirestore({ ...d.data(), id: d.id }))
  } catch (err) {
    console.error('Failed to load custom modules:', err)
    return []
  }
}

/**
 * Deletes a published custom module from Firestore (managers and admins).
 */
export async function deleteCustomModule(moduleId) {
  try {
    const docRef = doc(db, APP_COLLECTION, APP_DOC, 'custom_modules', String(moduleId))
    await deleteDoc(docRef)
    return { success: true }
  } catch (err) {
    console.error('Failed to delete custom module:', err)
    return { success: false, error: err.message }
  }
}

/**
 * Deletes an in-progress draft from Firestore.
 */
export async function deleteDraft(draftId) {
  try {
    const docRef = doc(db, APP_COLLECTION, APP_DOC, 'module_drafts', String(draftId))
    await deleteDoc(docRef)
    return { success: true }
  } catch (err) {
    console.error('Failed to delete draft:', err)
    return { success: false, error: err.message }
  }
}

/**
 * Lists all module drafts saved in Firestore, deduplicating multiple revisions
 * of the same module and pruning stale orphan duplicates.
 */
export async function listDrafts(user) {
  try {
    const colRef = collection(db, APP_COLLECTION, APP_DOC, 'module_drafts')
    const snap = await getDocs(colRef)
    let drafts = snap.docs.map((d) => {
      const raw = d.data() || {}
      const data = hydrateFromFirestore({ draftId: d.id, ...raw })
      // Ensure currentSchema is normalized
      if (!data.currentSchema && (data.title || data.id)) {
        data.currentSchema = {
          id: data.id || d.id,
          title: data.title || 'Untitled Draft',
          subtitle: data.subtitle || '',
          track: data.track || 'General',
          pages: data.pages || [],
          quiz: data.quiz,
          interactive: data.interactive,
        }
      }
      return data
    })

    // Filter to user drafts if user is present
    if (user?.uid) {
      drafts = drafts.filter(
        (d) =>
          !d.updatedBy ||
          d.updatedBy === 'anon' ||
          d.updatedBy === user.uid ||
          (user.email && d.creatorEmail === user.email)
      )
    }

    // Sort newest first by updatedAt
    drafts.sort((a, b) => {
      const timeA = new Date(a.updatedAt || a.currentSchema?.updatedAt || 0).getTime()
      const timeB = new Date(b.updatedAt || b.currentSchema?.updatedAt || 0).getTime()
      return timeB - timeA
    })

    // Deduplicate across both canonical ID and normalized title
    const seenIds = new Set()
    const seenTitles = new Set()
    const uniqueDrafts = []
    const duplicateDraftIds = []

    for (const d of drafts) {
      const schema = d.currentSchema || {}
      const rawId = String(schema.id || d.draftId || d.id || '').trim()
      const rawTitle = String(schema.title || d.title || '').trim()
      const titleSlug = rawTitle.toLowerCase().replace(/[^a-z0-9]/g, '')

      let isDuplicate = false

      // Check ID match (if ID is a specific module ID)
      if (rawId && seenIds.has(rawId)) {
        isDuplicate = true
      }

      // Check Title match (if title is meaningful, > 3 alphanumeric chars)
      if (!isDuplicate && titleSlug.length > 3 && seenTitles.has(titleSlug)) {
        isDuplicate = true
      }

      if (isDuplicate) {
        duplicateDraftIds.push(d.draftId)
      } else {
        if (rawId) seenIds.add(rawId)
        if (d.draftId) seenIds.add(d.draftId)
        if (titleSlug.length > 3) seenTitles.add(titleSlug)
        uniqueDrafts.push(d)
      }
    }

    // Prune stale duplicates from Firestore in background
    if (duplicateDraftIds.length > 0) {
      Promise.all(duplicateDraftIds.map((id) => deleteDraft(id))).catch((e) =>
        console.warn('Could not prune duplicate drafts:', e)
      )
    }

    return uniqueDrafts
  } catch (err) {
    console.error('Failed to list drafts from Firestore:', err)
    return []
  }
}


/**
 * "That edit did not warrant a retake."
 *
 * The blanket rule is that changing a module asks everyone who completed it to
 * take it again, and that is right for a new question and wrong for a fixed
 * typo. This is the one escape hatch: it stamps a date the freshness check
 * treats as the baseline instead of `publishedAt`, so every completion up to
 * now counts again.
 *
 * It moves ONE date on ONE document. It does not touch anybody's progress,
 * which is what makes it safe to click and safe to get wrong — publishing
 * again re-arms the rule.
 */
export async function acceptCompletions(moduleId) {
  try {
    const docRef = doc(db, APP_COLLECTION, APP_DOC, 'custom_modules', String(moduleId))
    await setDoc(docRef, { completionsAcceptedAt: new Date().toISOString() }, { merge: true })
    return { success: true }
  } catch (err) {
    console.error('Failed to accept completions:', err)
    return { success: false, error: err.message }
  }
}

/**
 * Ask everyone who has completed this module to take it again.
 *
 * The manual counterpart to the freshness rule, for the modules the rule
 * cannot see: a BUILT-IN module's content lives in code, so it has no publish
 * timestamp and changing it is invisible to `contentFreshness`. Rather than
 * infer a change from a deploy — which touches every module and would ask
 * everyone to retake everything — an admin says so explicitly here.
 *
 * Writes the same fields as the per-trainee reissue in AdminPage, so a bulk
 * request and a single one are the same thing to every reader of the data.
 * `completed` is deleted and `quizResults` deliberately KEPT: the previous
 * score is the point of a retake, and KNOWN-ISSUES 1 records why.
 */
export async function reissueModuleForAllCompleters(moduleId) {
  const key = String(moduleId)
  try {
    const usersSnap = await getDocs(collection(db, APP_COLLECTION, APP_DOC, 'users'))
    const targets = []
    await Promise.all(usersSnap.docs.map(async (u) => {
      const progRef = doc(db, APP_COLLECTION, APP_DOC, 'users', u.id, 'progress', key)
      const snap = await getDoc(progRef)
      if (snap.exists() && snap.data()?.completed === true) targets.push(progRef)
    }))
    await Promise.all(targets.map((ref) => setDoc(ref, {
      reissued: serverTimestamp(),
      completed: deleteField(),
      completedAt: deleteField(),
      timeSpentMs: deleteField(),
      checkResult: deleteField(),
      quizState: deleteField(),
    }, { merge: true })))
    return { success: true, count: targets.length }
  } catch (err) {
    console.error('Failed to reissue module for all completers:', err)
    return { success: false, error: err.message }
  }
}
